import axios from 'axios'

/**
 * Uploading a file straight to MinIO over presigned urls (the storage service's multipart api).
 *
 * The bytes never pass through the storage service: it hands out a presigned url per part, the
 * browser PUTs each part itself and reports back only its etag. Ported from smazing's
 * frontend/packages/core/src/upload.ts.
 *
 * Finishes with the same object the chunked uploader does — `{fileid, extension, expired, status,
 * type: 'upload'}` — so the form posts it to Laravel and Laravel publishes it by `fileid`.
 */
const UPLOAD = {
  // Matches MULTIPART_UPLOAD.defaultChunkSize on the storage side: one part is one request body
  defaultChunkSize: 96 * 1024 * 1024,
  maxConcurrentParts: 4,
  maxRetries: 3,
  retryDelayMs: 1000,
  presignedUrlBatchSize: 50
}

const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms))

async function sha256Of(blob) {
  const digest = await crypto.subtle.digest('SHA-256', await blob.arrayBuffer())
  return Array.from(new Uint8Array(digest))
    .map((byte) => byte.toString(16).padStart(2, '0'))
    .join('')
}

export default class MultipartUpload {
  /**
   * @param {File} file
   * @param {{ endpoint: string, token: string, extension?: string, signal?: AbortSignal, onProgress?: Function }} options
   *        endpoint is the storage service's upload url (`.../api/v1/upload`)
   */
  constructor(file, options) {
    this.file = file
    this.options = options
    this.chunkSize = UPLOAD.defaultChunkSize
    this.sessionId = ''
    this.totalParts = 0
    this.bytesUploaded = 0
    this.bytesReported = 0
    this.completed = new Map()
    this.presigned = new Map()
  }

  async start() {
    await this.init()
    await this.uploadParts()
    const result = await this.request('post', `/${this.sessionId}/complete`, { verifyIntegrity: true })

    // The signature is `file-<expiresAt ms>-<payload>`: how long the upload waits to be published
    const expiresAt = parseInt(String(result.signature || '').split('-')[1], 10)
    return {
      fileid: result.fileId,
      extension: this.options.extension || this.file.name.split('.').pop(),
      expired: Math.floor((expiresAt || Date.now() + 3600 * 1000) / 1000),
      signature: result.signature,
      size: result.size,
      status: 'success',
      type: 'upload'
    }
  }

  async abort() {
    if (!this.sessionId) return
    try {
      await this.request('delete', `/${this.sessionId}`)
    } catch {
      // Nothing to do: the storage service sweeps abandoned uploads on its own
    }
  }

  async init() {
    const data = await this.request('post', '/init', {
      filename: this.file.name,
      contentType: this.file.type || 'application/octet-stream',
      fileSize: this.file.size,
      chunkSize: this.chunkSize
    })

    this.sessionId = data.sessionId
    this.totalParts = data.totalParts
    // The service may cut the file differently from what was asked for
    this.chunkSize = data.chunkSize
    for (const url of data.presignedUrls) this.presigned.set(url.partNumber, url)
  }

  async fetchUrls(startPart, count) {
    const urls = await this.request('get', `/${this.sessionId}/urls`, undefined, { startPart, count })
    for (const url of urls || []) this.presigned.set(url.partNumber, url)
  }

  /** A handful of parts at a time: more saturates the connection without helping */
  async uploadParts() {
    const queue = []
    for (let partNumber = 1; partNumber <= this.totalParts; partNumber++) {
      if (!this.completed.has(partNumber)) queue.push(partNumber)
    }

    const inFlight = new Set()
    while (queue.length || inFlight.size) {
      if (this.options.signal?.aborted) throw new Error('Upload aborted')

      while (queue.length && inFlight.size < UPLOAD.maxConcurrentParts) {
        const partNumber = queue.shift()
        if (!this.presigned.has(partNumber)) await this.fetchUrls(partNumber, Math.min(UPLOAD.presignedUrlBatchSize, this.totalParts - partNumber + 1))

        const promise = this.uploadPart(partNumber).finally(() => inFlight.delete(promise))
        inFlight.add(promise)
      }

      if (inFlight.size) await Promise.race(inFlight)
    }
  }

  async uploadPart(partNumber) {
    const start = (partNumber - 1) * this.chunkSize
    const end = Math.min(start + this.chunkSize, this.file.size)
    const chunk = this.file.slice(start, end)
    const sha256 = await sha256Of(chunk)

    let lastError
    for (let attempt = 0; attempt < UPLOAD.maxRetries; attempt++) {
      const url = this.presigned.get(partNumber)
      // A url is only signed for an hour, which a large upload outlives
      if (!url || Date.now() > url.expiresAt) {
        await this.fetchUrls(partNumber, 1)
        continue
      }

      try {
        const response = await axios.put(url.url, chunk, {
          headers: { 'Content-Type': 'application/octet-stream' },
          signal: this.options.signal,
          onUploadProgress: (event) => {
            if (!event.total) return
            this.report(this.bytesUploaded + (event.loaded / event.total) * (end - start))
          }
        })

        const etag = response.headers?.etag || response.headers?.['x-amz-etag'] || ''
        await this.request('post', `/${this.sessionId}/part-complete`, { partNumber, etag, clientSha256: sha256, bytesSent: end - start })

        this.completed.set(partNumber, { partNumber, etag })
        this.bytesUploaded += end - start
        this.report(this.bytesUploaded)
        return
      } catch (error) {
        lastError = error
        // MinIO answers an expired signature with a 403
        if (error?.response?.status === 403) await this.fetchUrls(partNumber, 1)
        if (attempt < UPLOAD.maxRetries - 1) await delay(UPLOAD.retryDelayMs * 2 ** attempt)
      }
    }

    throw lastError || new Error(`Failed to upload part ${partNumber}`)
  }

  /** Never backwards: several parts are in flight, and a bar that jumps back reads as broken */
  report(bytesSent) {
    if (!this.options.onProgress) return
    const sent = Math.min(bytesSent, this.file.size)
    if (sent < this.bytesReported) return
    this.bytesReported = sent
    this.options.onProgress(Math.floor((sent / this.file.size) * 100))
  }

  async request(method, path, data, params) {
    const response = await axios.request({
      method,
      url: `${this.options.endpoint.replace(/\/+$/, '')}${path}`,
      data,
      params,
      headers: { Authorization: `Bearer ${this.options.token}` },
      signal: this.options.signal
    })
    return response.data?.data ?? response.data
  }
}
